// Cómo se leen las reglas de comisión en pantalla: palancas, condición, alcance, estado y acciones.
// Mockup: surekeep/mockups/backoffice/portal-admin-vl-comisiones.html
import type { RetailCommission } from '@fiado/type-kit';
import { CommissionBonusModeEnum } from '@fiado/type-kit/bin/retailCommission/enums/CommissionBonusModeEnum';
import { CommissionCountByEnum } from '@fiado/type-kit/bin/retailCommission/enums/CommissionCountByEnum';
import { CommissionMetricEnum } from '@fiado/type-kit/bin/retailCommission/enums/CommissionMetricEnum';
import { CommissionOperatorEnum } from '@fiado/type-kit/bin/retailCommission/enums/CommissionOperatorEnum';
import { CommissionRuleStatusEnum } from '@fiado/type-kit/bin/retailCommission/enums/CommissionRuleStatusEnum';
import type { ProductCategoryEnum } from '@fiado/type-kit/bin/retailCatalog/enums/ProductCategoryEnum';
import type { ProductTierEnum } from '@fiado/type-kit/bin/retailCatalog/enums/ProductTierEnum';
import {
    describeValidityCountdown,
    LIFECYCLE_STATUS_TONE,
    lifecycleLabels,
    matchesNameSearch,
    type LifecycleStatus,
} from '@/shared/patterns/Lifecycle';
import { formatDayMx } from '@/lib/dateHelpers';
import type { PillTone } from '@/shared/patterns/Pills';
import { sellerPaidByRule } from './payoutRows';

type Rule = RetailCommission.CommissionRule;
type Levers = RetailCommission.CommissionRuleLevers;
type Condition = RetailCommission.CommissionRuleCondition;
type Scope = RetailCommission.CommissionRuleScope;
type Breakdown = RetailCommission.CommissionSellerBreakdown;
type Status = `${RetailCommission.CommissionRuleStatusEnum}`;
type Metric = `${RetailCommission.CommissionMetricEnum}`;
type Operator = `${RetailCommission.CommissionOperatorEnum}`;
type CountBy = `${RetailCommission.CommissionCountByEnum}`;
type PayTiming = `${RetailCommission.CommissionPayTimingEnum}`;

/** Lo que cuenta la regla: sin dato, piezas. */
const countsSales = (countBy: CountBy | undefined) => countBy === CommissionCountByEnum.SALE;

const { PERCENT } = CommissionBonusModeEnum;
const { COLOCACION_PERIODO, NPI_M30_VENDEDOR, POSICION_VENTA_EN_PERIODO } = CommissionMetricEnum;
const { BETWEEN, IN } = CommissionOperatorEnum;
const { ACTIVE, SCHEDULED, PAUSED, DRAFT, EXPIRED } = CommissionRuleStatusEnum;

const NUMBER = new Intl.NumberFormat('es-MX', { maximumFractionDigits: 2 });

/** Centavos a pesos sin decimales de relleno: `$80`, `$1,500`, `$12.5`. */
export const pesos = (cents: number) => `$${NUMBER.format(cents / 100)}`;
/** Puntos base a porcentaje: 250 → `2.5%`. */
export const percent = (bps: number) => `${NUMBER.format(bps / 100)}%`;
/** Puntos base a factor: 15000 → `×1.5`. */
export const factor = (bps: number) => `×${NUMBER.format(bps / 10_000)}`;

/** Cada estado de una regla ya es un estado visual común del ciclo de vida. */
export const STATUS_LIFECYCLE: Record<Status, LifecycleStatus> = {
    ACTIVE: 'ACTIVE',
    SCHEDULED: 'SCHEDULED',
    PAUSED: 'PAUSED',
    DRAFT: 'DRAFT',
    EXPIRED: 'EXPIRED',
};

export const STATUS_LABEL: Record<Status, string> = lifecycleLabels(STATUS_LIFECYCLE);

export const STATUS_TONE: Record<Status, PillTone> = {
    ACTIVE: LIFECYCLE_STATUS_TONE.ACTIVE,
    SCHEDULED: LIFECYCLE_STATUS_TONE.SCHEDULED,
    PAUSED: LIFECYCLE_STATUS_TONE.PAUSED,
    DRAFT: LIFECYCLE_STATUS_TONE.DRAFT,
    EXPIRED: LIFECYCLE_STATUS_TONE.EXPIRED,
};

export const CATEGORY_LABEL: Record<`${ProductCategoryEnum}`, string> = {
    PHONE: 'Teléfono',
    SIM: 'SIM',
    MODEM: 'Módem',
    ACCESSORY: 'Accesorio',
    BUNDLE: 'Kit',
};

export const TIER_LABEL: Record<`${ProductTierEnum}`, string> = {
    PREMIUM: 'Premium',
    HIGH: 'Alta',
    MID: 'Media',
    LOW: 'Baja',
};

export const WINDOW_LABEL: Record<`${RetailCommission.CommissionWindowEnum}`, string> = {
    SEMANA_OPERATIVA: 'Semana operativa',
    ROLLING_7D: 'Últimos 7 días',
    MES: 'Mes calendario',
};

export const PAY_TIMING_LABEL: Record<PayTiming, string> = {
    ON_SALE: 'Al momento',
    ON_CUT: 'Al cierre de la semana',
    MONTHLY: 'Al cierre del mes',
};

/** En la línea de palancas solo se anota lo que no se paga al cierre de la semana. */
const PAY_TIMING_NOTE: Partial<Record<PayTiming, string>> = {
    ON_SALE: 'paga al momento',
    MONTHLY: 'paga al cierre del mes',
};

export const BILLING_PERIOD_LABEL: Record<
    `${RetailCommission.CommissionBillingPeriodEnum}`,
    string
> = {
    WEEKLY: 'Semanal',
    MONTHLY: 'Mensual',
};

const STATUS_ORDER: readonly Status[] = ['ACTIVE', 'SCHEDULED', 'PAUSED', 'DRAFT', 'EXPIRED'];

/** Activas primero y vencidas al final; dentro de cada estado, el orden del motor (la más nueva primero). */
export function sortRules(rules: readonly Rule[]): Rule[] {
    return [...rules].sort(
        (a, b) => STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status),
    );
}

/** La más reciente de dos copias de una regla: más versión o, con la misma, cambiada después. */
export function newerRule(a: Rule | null | undefined, b: Rule | null | undefined): Rule | null {
    if (!a || !b) return a ?? b ?? null;
    if (a.version !== b.version) return a.version > b.version ? a : b;
    return b.updatedAt > a.updatedAt ? b : a;
}

export type StatusFilter = 'ALL' | Status;

export function filterRules(rules: readonly Rule[], search: string, status: StatusFilter): Rule[] {
    const query = search.trim().toLowerCase();
    return rules.filter((r) => {
        const ruleStatus: Status = r.status;
        return (
            (status === 'ALL' || ruleStatus === status) &&
            (matchesNameSearch(r.name, search) || r.ruleId.toLowerCase().includes(query))
        );
    });
}

/** Los estados del filtro, en el orden operativo de la columna. */
export const STATUS_FILTER_ORDER: readonly Status[] = [
    'ACTIVE',
    'SCHEDULED',
    'PAUSED',
    'DRAFT',
    'EXPIRED',
];

/** La vigencia de una regla en una línea. */
export function describeRuleValidity(rule: Pick<Rule, 'effectiveFrom' | 'effectiveTo'>): string {
    const from = formatDayMx(rule.effectiveFrom);
    return rule.effectiveTo === null
        ? `Desde ${from} · sin fin`
        : `${from} – ${formatDayMx(rule.effectiveTo)}`;
}

/** La vigencia corta de la lista: «Vence en 8 días», «Permanente», «Arranca mañana». */
export function describeRuleCountdown(
    rule: Pick<Rule, 'effectiveFrom' | 'effectiveTo'>,
    today: string,
): string {
    return describeValidityCountdown(rule.effectiveFrom, rule.effectiveTo, today);
}

/** Cuándo se paga el resto de un adelanto. */
export const ADVANCE_REST: Record<PayTiming, string> = {
    ON_SALE: '',
    ON_CUT: 'al cierre de la semana',
    MONTHLY: 'el día 1 del mes siguiente',
};

/** `advanceBps` solo cuenta si el resto se paga al cierre; "al momento" ya paga todo al vender. */
export function describeLevers(
    levers: Levers,
    countBy?: CountBy,
    payTiming?: PayTiming,
    advanceBps?: number | null,
): string {
    const parts: string[] = [];
    const sale = levers.bonoPorVenta;
    const per = countsSales(countBy) ? 'venta' : 'pieza';
    if (sale)
        parts.push(
            sale.mode === PERCENT
                ? `+${percent(sale.value)}/${per}`
                : `+${pesos(sale.value)}/${per}`,
        );
    if (levers.bonoUmbral) parts.push(`+${pesos(levers.bonoUmbral.amountCents)} umbral`);
    if (levers.multiplicador) parts.push(factor(levers.multiplicador.factorBps));
    const coupon = levers.bonoPorCuponPagado;
    if (coupon)
        parts.push(
            coupon.mode === PERCENT
                ? `${percent(coupon.value)}/cupón`
                : `+${pesos(coupon.value)}/cupón`,
        );
    if (parts.length === 0) return '—';
    const timing = payTiming ?? 'ON_CUT';
    const note =
        advanceBps != null && timing !== 'ON_SALE'
            ? `${String(advanceBps / 100)}% al momento y el resto ${ADVANCE_REST[timing]}`
            : payTiming === undefined
              ? undefined
              : PAY_TIMING_NOTE[payTiming];
    return [...parts, ...(note === undefined ? [] : [note])].join(' · ');
}

/** El tipo que se deduce de las palancas, como `deducirTipo` del mock. */
export function ruleKind(levers: Levers): string {
    const filled = [
        levers.bonoPorVenta,
        levers.bonoUmbral,
        levers.multiplicador,
        levers.bonoPorCuponPagado,
    ].filter((lever) => lever !== null);
    if (filled.length === 0) return 'Sin palanca';
    if (filled.length > 1) return 'Combo';
    if (levers.bonoPorVenta)
        return levers.bonoPorVenta.mode === PERCENT ? '% del precio' : 'Bono/venta';
    if (levers.bonoUmbral) return 'Bono umbral';
    if (levers.multiplicador) return 'Multiplicador';
    return levers.bonoPorCuponPagado?.mode === PERCENT ? '% del cupón' : 'Bono/cupón';
}

const METRIC_LABEL: Record<Metric, string> = {
    POSICION_VENTA_EN_PERIODO: 'piezas',
    COLOCACION_PERIODO: 'Monto',
    NPI_M30_VENDEDOR: 'Cartera NPI',
    SKUS_DISTINTOS_PERIODO: 'Modelos',
};

const OPERATOR_SYMBOL: Record<Exclude<Operator, 'BETWEEN' | 'IN'>, string> = {
    GTE: '≥',
    LTE: '≤',
    LT: '<',
    GT: '>',
};

/** Un valor de la condición en las unidades de su métrica. */
function conditionValueText(metric: CommissionMetricEnum, value: number): string {
    if (metric === COLOCACION_PERIODO) return pesos(value);
    if (metric === NPI_M30_VENDEDOR) return percent(value);
    return NUMBER.format(value);
}

export function describeCondition(condition: Condition | null, countBy?: CountBy): string {
    if (!condition) return 'Siempre';
    const { metric, operator, minSample } = condition;
    const values = Array.isArray(condition.value) ? condition.value : [condition.value];
    const text = (value: number) => conditionValueText(metric, value);
    const range =
        operator === BETWEEN
            ? `${text(values[0])}-${text(values[1])}`
            : operator === IN
              ? `en ${values.map(text).join(', ')}`
              : `${OPERATOR_SYMBOL[operator]} ${text(values[0])}`;
    const sample =
        metric === NPI_M30_VENDEDOR && minSample !== null
            ? ` · mín. ${String(minSample)} créd.`
            : '';
    const label =
        metric === POSICION_VENTA_EN_PERIODO && countsSales(countBy)
            ? 'ventas'
            : METRIC_LABEL[metric];
    return `${label} ${range}${sample}`;
}

export function describeScope(scope: Scope): string {
    const parts: string[] = [];
    if (scope.tipos.length > 0)
        parts.push(scope.tipos.map((tipo) => CATEGORY_LABEL[tipo]).join(' + '));
    if (scope.gamas.length > 0)
        parts.push(`Gama ${scope.gamas.map((gama) => TIER_LABEL[gama]).join('/')}`);
    if (scope.skus.length > 0)
        parts.push(`${String(scope.skus.length)} ${scope.skus.length === 1 ? 'SKU' : 'SKUs'}`);
    if (scope.tiendas.length > 0) {
        parts.push(
            `${String(scope.tiendas.length)} ${scope.tiendas.length === 1 ? 'tienda' : 'tiendas'}`,
        );
    }
    return parts.length > 0 ? parts.join(' · ') : 'Todo el catálogo';
}

/** Sensible al NPI M30: la renta de cartera o un multiplicador condicionado al NPI. */
export function isNpiSensitive(rule: Pick<Rule, 'levers' | 'condition'>): boolean {
    return (
        rule.levers.bonoPorCuponPagado !== null ||
        (rule.levers.multiplicador !== null && rule.condition?.metric === NPI_M30_VENDEDOR)
    );
}

export type RuleMenuAction =
    | 'publish'
    | 'pause'
    | 'resume'
    | 'expire'
    | 'duplicate'
    | 'simulate'
    | 'delete';

/** Lo que el motor permite en cada estado. Publicar aparece mientras haya una versión sin publicar. */
export function menuActionsFor(
    rule: Pick<Rule, 'status' | 'version' | 'publishedVersion'>,
): RuleMenuAction[] {
    const { status } = rule;
    const actions: RuleMenuAction[] = [];
    if (status !== EXPIRED && rule.publishedVersion !== rule.version) actions.push('publish');
    if (status === ACTIVE || status === SCHEDULED) actions.push('pause');
    if (status === PAUSED) actions.push('resume');
    if (status === ACTIVE || status === SCHEDULED || status === PAUSED) actions.push('expire');
    actions.push('duplicate', 'simulate');
    if (status === DRAFT) actions.push('delete');
    return actions;
}

/** Si el motor ya aplica lo que muestra el editor y, si no, qué versión aplica. */
export type PublishState =
    | { kind: 'live' }
    | { kind: 'draft' }
    | { kind: 'pending'; appliedVersion: number };

export function publishStateOf(
    rule: Pick<Rule, 'status' | 'version' | 'publishedVersion'>,
): PublishState {
    if (rule.status === EXPIRED || rule.publishedVersion === rule.version) return { kind: 'live' };
    if (rule.status === DRAFT || rule.publishedVersion === null) return { kind: 'draft' };
    return { kind: 'pending', appliedVersion: rule.publishedVersion };
}

/** Una regla que ya aplica se publica al guardar. Pausada no: publicarla la reactiva. */
export function savePublishes(rule: Pick<Rule, 'status'> | null): boolean {
    return rule !== null && (rule.status === ACTIVE || rule.status === SCHEDULED);
}

/** Lo pagado por regla en un corte: el bono más su parte del multiplicador, y cada umbral y renta en su regla. */
export function paidByRule(sellers: readonly Breakdown[]): Map<string, number> {
    const paid = new Map<string, number>();
    for (const seller of sellers)
        for (const r of sellerPaidByRule(seller))
            paid.set(r.ruleId, (paid.get(r.ruleId) ?? 0) + r.paidCents);
    return paid;
}
